import React, { useRef } from "react";
import Star from "./star";

interface StarProgressBarProps {
  value: number;
  setValue: (value: number) => void;
  min?: number;
  max?: number;
  showValue?: boolean;
}

const StarProgressBar: React.FC<StarProgressBarProps> = ({
  value,
  setValue,
  min = 1,
  max = 10000,
  showValue = false,
}) => {
  const barRef = useRef<HTMLDivElement | null>(null);

  // Calculate percentage position from value
  const percentage = ((value - min) / (max - min)) * 100;

  const updateValueFromPosition = (clientX: number) => {
    if (!barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    // Calculate position relative to the track (full width)
    const trackWidth = rect.width;
    const clickPosition = clientX - rect.left;
    let newPercentage = (clickPosition / trackWidth) * 100;
    newPercentage = Math.max(0, Math.min(100, newPercentage));
    // Convert percentage back to value
    const newValue = Math.round(min + (newPercentage / 100) * (max - min));
    setValue(Math.max(min, Math.min(max, newValue)));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    updateValueFromPosition(e.clientX);

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      updateValueFromPosition(e.clientX);
    };

    const handleMouseUp = () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    updateValueFromPosition(e.touches[0].clientX);

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      updateValueFromPosition(e.touches[0].clientX);
    };

    const handleTouchEnd = () => {
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };

    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", handleTouchEnd);
  };

  return (
    <div className="relative w-full max-w-lg h-8 mt-5 " ref={barRef}>
      <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-500 rounded -translate-y-1/2 z-0" />
      <div
        className="absolute top-1/2 left-0 h-1 bg-blue-500 rounded -translate-y-1/2 z-10"
        style={{ width: `${percentage}%` }}
      />
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 cursor-pointer select-none touch-none"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        style={{
          left: `${percentage}%`,
          touchAction: "none",
        }}
      >
        <Star className="w-8 h-8" />
      </div>
      {showValue && (
        <div className="absolute -top-9 left-1/2 -translate-x-1/2 text-[24px] text-[#FFD900] font-spline-sans-mono">
          {value}
        </div>
      )}
    </div>
  );
};

export default StarProgressBar;
